import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Divider from '@mui/joy/Divider';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Switch from '@mui/joy/Switch';

import CloseIcon from '@mui/icons-material/Close';
import DataObjectIcon from '@mui/icons-material/DataObject';
import CodeIcon from '@mui/icons-material/Code';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import OutputIcon from '@mui/icons-material/Output';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { themeColors, shadows } from '../../styles/theme';
import { NODE_CONFIG, OUTPUT_FORMAT_CONFIG } from '../../config/nodeConfig';
import DocxTemplateUpload from '../DocxTemplateUpload';
import ExcelTemplateUpload from '../ExcelTemplateUpload';
import SchemaEditor from './SchemaEditor';

export default function ModernNodePanel({
  visible,
  selectedNode,
  onClose,
  onNodeNameChange,
  onOpenPromptEditor,
  onOpenCodeEditor,
  onOpenTemplateEditor,
  onOpenExamplesEditor,
  onNodeUpdate,
  onBaseOutputToggle,
  nodeInputs = [],
  nodeOutputs = [],
  width = 280,
  onResize,
}) {
  const [nodeName, setNodeName] = useState('');
  const [showContract, setShowContract] = useState(false);

  useEffect(() => {
    setNodeName(selectedNode?.data?.label || '');
  }, [selectedNode]);

  const handleNameChange = (e) => {
    setNodeName(e.target.value);
  };

  const handleNameBlur = () => {
    if (selectedNode && nodeName !== selectedNode.data.label) {
      onNodeNameChange(selectedNode.id, nodeName);
    }
  };

  const handleOutputFormatChange = (e, value) => {
    if (selectedNode && onNodeUpdate) {
      const updatedNode = {
        ...selectedNode,
        data: { ...selectedNode.data, output_format: value },
      };
      onNodeUpdate(updatedNode);
    }
  };

  const handleSchemaChange = (newSchema) => {
    if (selectedNode && onNodeUpdate) {
      onNodeUpdate({
        ...selectedNode,
        data: { ...selectedNode.data, output_schema: newSchema },
      });
    }
  };

  if (!visible || !selectedNode) {
    return null;
  }

  const config = NODE_CONFIG[selectedNode.type];
  if (!config) return null;

  const Icon = config.icon;

  return (
    <Box
      sx={{
        width: width,
        height: '100%',
        backgroundColor: 'white',
        borderLeft: `1px solid ${themeColors.neutral[200]}`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        animation: 'slideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes slideIn': {
          '0%': {
            transform: 'translateX(100%)',
            opacity: 0,
          },
          '100%': {
            transform: 'translateX(0)',
            opacity: 1,
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: themeColors.neutral[25],
          borderBottom: `1px solid ${themeColors.neutral[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: `${config.colors.border}15`,
            }}
          >
            <Icon sx={{ fontSize: 16, color: config.colors.border }} />
          </Box>
          <Typography
            level="title-sm"
            sx={{
              fontWeight: 700,
              color: themeColors.neutral[900],
              fontSize: '0.8125rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Properties
          </Typography>
        </Box>

        <IconButton
          size="sm"
          onClick={onClose}
          sx={{
            width: 24,
            height: 24,
            minHeight: 24,
            backgroundColor: themeColors.neutral[100],
            border: 'none',
            '&:hover': {
              backgroundColor: themeColors.neutral[200],
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Node Name */}
        <Box sx={{ mb: 3 }}>
          <Typography
            level="body-xs"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: themeColors.neutral[700],
              fontSize: '0.75rem',
            }}
          >
            NAME
          </Typography>
          <Input
            value={nodeName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            size="sm"
            placeholder="Enter node name..."
            sx={{
              border: `1px solid ${themeColors.neutral[200]}`,
              borderRadius: 1.5,
              backgroundColor: 'white',
              fontSize: '0.8125rem',
              '--Input-focusedThickness': '0px',
              '&:hover': {
                borderColor: themeColors.neutral[300],
              },
              '&:focus-within': {
                borderColor: themeColors.primary[500],
              },
              transition: 'all 0.15s ease',
            }}
          />
        </Box>

        {/* Base Output Toggle (for ai, code, template nodes) */}
        {['ai_node', 'code_node', 'template_node'].includes(selectedNode.type) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              level="body-xs"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: themeColors.neutral[700],
                fontSize: '0.75rem',
              }}
            >
              BASE OUTPUT
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: `1px solid ${themeColors.neutral[200]}`,
                borderRadius: 1.5,
                backgroundColor: selectedNode.data.is_base_output ? '#fffbeb' : 'white',
                transition: 'all 0.15s ease',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DataObjectIcon sx={{ fontSize: 16, color: selectedNode.data.is_base_output ? '#d97706' : themeColors.neutral[500] }} />
                <Box>
                  <Typography level="body-sm" sx={{ fontSize: '0.8125rem', fontWeight: 500, color: themeColors.neutral[800] }}>
                    Base output node
                  </Typography>
                  <Typography level="body-xs" sx={{ fontSize: '0.6875rem', color: themeColors.neutral[500] }}>
                    Output must be valid JSON
                  </Typography>
                </Box>
              </Box>
              <Switch
                size="sm"
                checked={!!selectedNode.data.is_base_output}
                onChange={(e) => onBaseOutputToggle(selectedNode.id, e.target.checked)}
                sx={{
                  '--Switch-trackWidth': '32px',
                  '--Switch-trackHeight': '18px',
                  '--Switch-thumbSize': '14px',
                }}
              />
            </Box>

            {selectedNode.data.is_base_output && (
              <Box sx={{ mt: 1 }}>
                <Box
                  onClick={() => setShowContract(v => !v)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    color: themeColors.neutral[500],
                    '&:hover': { color: themeColors.neutral[700] },
                    transition: 'color 0.15s ease',
                    userSelect: 'none',
                    py: 0.5,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                  <Typography level="body-xs" sx={{ fontSize: '0.6875rem', fontWeight: 500, flex: 1 }}>
                    Stateful contract
                  </Typography>
                  {showContract
                    ? <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                    : <KeyboardArrowRightIcon sx={{ fontSize: 14 }} />
                  }
                </Box>

                {showContract && (
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: themeColors.neutral[50],
                      border: `1px solid ${themeColors.neutral[200]}`,
                      fontFamily: 'monospace',
                      fontSize: '0.6875rem',
                      color: themeColors.neutral[700],
                      lineHeight: 1.6,
                      whiteSpace: 'pre',
                    }}
                  >{`[
  {
    "action": "SET",
    "key": "field_name",
    "value": <any>
  },
  {
    "action": "DELETE",
    "key": "field_name"
  }
]`}</Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Output Schema (for ai, code, template nodes) */}
        {['ai_node', 'code_node', 'template_node'].includes(selectedNode.type) && (
          <Box sx={{ mb: 3 }}>
            <Typography
              level="body-xs"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: themeColors.neutral[700],
                fontSize: '0.75rem',
              }}
            >
              OUTPUT SCHEMA
            </Typography>
            <SchemaEditor
              schema={selectedNode.data.output_schema ?? null}
              onChange={handleSchemaChange}
            />
          </Box>
        )}

        {/* Output Format (for output nodes) */}
        {selectedNode.type === 'output_node' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography
                level="body-xs"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: themeColors.neutral[700],
                  fontSize: '0.75rem',
                }}
              >
                OUTPUT FORMAT
              </Typography>
              <Select
                value={selectedNode.data.output_format || 'markdown_to_pdf'}
                onChange={handleOutputFormatChange}
                size="sm"
                sx={{
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  backgroundColor: 'white',
                  fontSize: '0.8125rem',
                  '&:hover': {
                    borderColor: themeColors.neutral[300],
                  },
                  '--Select-focusedThickness': '0px',
                  '&:focus-within': {
                    borderColor: themeColors.primary[500],
                  },
                }}
              >
                {Object.entries(OUTPUT_FORMAT_CONFIG).map(([key, formatConfig]) => (
                  <Option key={key} value={key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 0.5,
                          backgroundColor: `${formatConfig.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {formatConfig.icon({ sx: { fontSize: 11, color: formatConfig.color } })}
                      </Box>
                      <Typography level="body-sm" sx={{ fontSize: '0.8125rem' }}>
                        {formatConfig.label}
                      </Typography>
                    </Box>
                  </Option>
                ))}
              </Select>
            </Box>

            {/* Template Upload (for formats that require a template) */}
            {OUTPUT_FORMAT_CONFIG[selectedNode.data.output_format]?.requiresTemplate && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  level="body-xs"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: themeColors.neutral[700],
                    fontSize: '0.75rem',
                  }}
                >
                  TEMPLATE
                </Typography>

                {selectedNode.data.output_format === 'xlsx' ? (
                  <ExcelTemplateUpload
                    currentTemplate={selectedNode.data.excel_config?.template instanceof File ? selectedNode.data.excel_config.template : null}
                    templateUrl={selectedNode.data.excel_config?.template_url || null}
                    onTemplateUpload={(file) => {
                      onNodeUpdate({
                        ...selectedNode,
                        data: {
                          ...selectedNode.data,
                          excel_config: { ...selectedNode.data.excel_config, template: file },
                        },
                      });
                    }}
                    onTemplateRemove={() => {
                      onNodeUpdate({
                        ...selectedNode,
                        data: {
                          ...selectedNode.data,
                          excel_config: { template: null, template_url: null },
                        },
                      });
                    }}
                  />
                ) : (
                  <DocxTemplateUpload
                    currentTemplate={selectedNode.data.docx_config?.template instanceof File ? selectedNode.data.docx_config.template : null}
                    templateUrl={selectedNode.data.docx_config?.template_url || null}
                    photoWidthMm={selectedNode.data.docx_config?.photo_width_mm ?? 120}
                    onTemplateUpload={(file) => {
                      onNodeUpdate({
                        ...selectedNode,
                        data: {
                          ...selectedNode.data,
                          docx_config: { ...selectedNode.data.docx_config, template: file },
                        },
                      });
                    }}
                    onTemplateRemove={() => {
                      onNodeUpdate({
                        ...selectedNode,
                        data: {
                          ...selectedNode.data,
                          docx_config: { template: null, template_url: null, photo_width_mm: selectedNode.data.docx_config?.photo_width_mm ?? 120 },
                        },
                      });
                    }}
                    onPhotoWidthChange={(width) => {
                      onNodeUpdate({
                        ...selectedNode,
                        data: {
                          ...selectedNode.data,
                          docx_config: { ...selectedNode.data.docx_config, photo_width_mm: width },
                        },
                      });
                    }}
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* Connections */}
        {(nodeInputs.length > 0 || nodeOutputs.length > 0) && (
          <>
            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ mb: 2.5 }}>
              <Typography
                level="body-xs"
                sx={{
                  fontWeight: 600,
                  mb: 1.5,
                  color: themeColors.neutral[700],
                  fontSize: '0.75rem',
                }}
              >
                CONNECTIONS
              </Typography>

              {nodeInputs.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.neutral[600],
                      mb: 0.75,
                      fontSize: '0.6875rem',
                    }}
                  >
                    Inputs ({nodeInputs.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {nodeInputs.map((input, idx) => (
                      <Chip
                        key={idx}
                        size="sm"
                        sx={{
                          backgroundColor: themeColors.neutral[100],
                          color: themeColors.neutral[700],
                          fontWeight: 500,
                          fontSize: '0.6875rem',
                          height: 22,
                        }}
                      >
                        {input}
                      </Chip>
                    ))}
                  </Box>
                </Box>
              )}

              {nodeOutputs.length > 0 && (
                <Box>
                  <Typography
                    level="body-xs"
                    sx={{
                      color: themeColors.neutral[600],
                      mb: 0.75,
                      fontSize: '0.6875rem',
                    }}
                  >
                    Outputs ({nodeOutputs.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {nodeOutputs.map((output, idx) => (
                      <Chip
                        key={idx}
                        size="sm"
                        sx={{
                          backgroundColor: themeColors.neutral[100],
                          color: themeColors.neutral[700],
                          fontWeight: 500,
                          fontSize: '0.6875rem',
                          height: 22,
                        }}
                      >
                        {output}
                      </Chip>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider sx={{ my: 2.5 }} />

        {/* Edit Actions */}
        <Box>
          <Typography
            level="body-xs"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: themeColors.neutral[700],
              fontSize: '0.75rem',
            }}
          >
            EDIT CONTENT
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {selectedNode.type === 'ai_node' && (
              <>
                <Button
                  startDecorator={<SmartToyIcon sx={{ fontSize: 16 }} />}
                  onClick={onOpenPromptEditor}
                  size="sm"
                  variant="plain"
                  sx={{
                    justifyContent: 'flex-start',
                    px: 1.5,
                    py: 1,
                    backgroundColor: 'white',
                    border: `1px solid ${themeColors.neutral[200]}`,
                    borderRadius: 1.5,
                    color: themeColors.neutral[800],
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    '&:hover': {
                      backgroundColor: 'white',
                      borderColor: themeColors.neutral[300],
                    },
                  }}
                >
                  Edit Prompt
                </Button>
              </>
            )}

            {selectedNode.type === 'code_node' && (
              <Button
                startDecorator={<CodeIcon sx={{ fontSize: 16 }} />}
                onClick={onOpenCodeEditor}
                size="sm"
                variant="plain"
                sx={{
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 1,
                  backgroundColor: 'white',
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  color: themeColors.neutral[800],
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  '&:hover': {
                    backgroundColor: themeColors.neutral[25],
                    borderColor: themeColors.neutral[300],
                  },
                }}
              >
                Edit Code
              </Button>
            )}

            {selectedNode.type === 'template_node' && (
              <Button
                startDecorator={<DescriptionIcon sx={{ fontSize: 16 }} />}
                onClick={onOpenTemplateEditor}
                size="sm"
                variant="plain"
                sx={{
                  justifyContent: 'flex-start',
                  px: 1.5,
                  py: 1,
                  backgroundColor: 'white',
                  border: `1px solid ${themeColors.neutral[200]}`,
                  borderRadius: 1.5,
                  color: themeColors.neutral[800],
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  '&:hover': {
                    backgroundColor: themeColors.neutral[25],
                    borderColor: themeColors.neutral[300],
                  },
                }}
              >
                Edit Template
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Resize Handle */}
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          top: 0,
          left: -2,
          width: 4,
          height: '100%',
          cursor: 'ew-resize',
          zIndex: 10,
          '&:hover': {
            backgroundColor: themeColors.primary[500],
          },
          transition: 'background-color 0.15s ease',
        }}
      />
    </Box>
  );
}
